import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import InvoiceFromSaleModal from '../components/InvoiceFromSaleModal';
import httpRequest from '../utils/request';
import buildNonFiscalTicketHtml from '../utils/ticketTemplate';

const SalesHistory = () => {
  const { user } = useSelector((state) => state.auth);
  const { tenant } = useSelector((state) => state.tenant || {});
  const { fiscalStatus, fiscalConfig } = useSelector((state) => state.fiscal || {});

  const emitterIsMonotributo = (() => {
    try {
      const fiscal = fiscalConfig || fiscalStatus;
      if (typeof fiscal?.discriminarIVA !== 'undefined') {
        if (fiscal.discriminarIVA === false) return true;
      }
      if (typeof fiscal?.regimenFiscal === 'string' && /mono/i.test(fiscal.regimenFiscal)) return true;
      const cond = fiscal?.condicionIVA ?? fiscal?.condicion ?? fiscal?.tipoResponsable;
      if (cond != null && Number(cond) === 6) return true;
      if (typeof cond === 'string' && /monotr/i.test(cond)) return true;
      return false;
    } catch (e) { return false; }
  })();

  const serviceType = user?.tenantId?.serviceType || 'full_pos';
  const isEcommerceLite = serviceType === 'ecommerce_lite';

  const navigate = useNavigate();

  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSale, setExpandedSale] = useState(null);

  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterSource, setFilterSource] = useState('all');
  
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [limit] = useState(20);

  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);

  // Traer ventas con paginación y filtros
  const fetchSales = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page,
        limit,
        startDate: filterDateFrom,
        endDate: filterDateTo,
        source: filterSource
      });

      const res = await httpRequest.get(`/sales/all?${params.toString()}`);
      
      if (res.data && res.data.items) {
        setSales(res.data.items);
        setTotalItems(res.data.total);
        setTotalAmount(res.data.totalAmount || 0);
        setTotalPages(res.data.pages);
        setCurrentPage(res.data.currentPage);
      } else if (Array.isArray(res.data)) {
        // Fallback para modo legacy si el backend no devolviera el objeto con items
        setSales(res.data);
      }
    } catch (err) {
      console.error('Error al traer ventas:', err.response?.data || err.message);
      toast.error('❌ Error al cargar historial de ventas');
    } finally {
      setLoading(false);
    }
  }, [filterDateFrom, filterDateTo, filterSource, limit]);

  useEffect(() => {
    fetchSales(1);
  }, [fetchSales]);

  // Resolver nombre del producto directo del item gracias al popular del backend
  const getProductName = (item) => {
    return item.name || `Producto ${item.productId}`;
  };

  // Filtrar ya no es necesario en el frontend, usamos los de backend
  // Pero necesitamos el contador de pedidos online para el botón de filtro
  // Esto es un poco delicado si está paginado. 
  // Podríamos tener un endpoint de "stats" separado o el backend podría devolverlo.
  // Por ahora lo dejamos como está o lo deshabilitamos si no tenemos el total real.
  // Vamos a asumir que el backend podría enviarlo en el futuro o simplemente no mostrar el badge si no tenemos el dato exacto de TODO el universo.
  
  const clearFilters = () => {
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterSource('all');
    setCurrentPage(1);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const toggleExpand = (saleId) => {
    setExpandedSale(expandedSale === saleId ? null : saleId);
  };

  const cancelSale = async (saleId) => {
    if (!window.confirm('¿Está seguro de que desea cancelar esta venta? Se eliminará completamente y se restaurará el stock de los productos.')) {
      return;
    }

    try {
      await httpRequest.delete(`/sales/cancel/${saleId}`);
      toast.success('✅ Venta cancelada exitosamente');
      // Recargar las ventas para reflejar los cambios
      fetchSales();
    } catch (err) {
      console.error('Error cancelando venta:', err);
      toast.error('❌ Error al cancelar la venta: ' + (err.response?.data?.message || err.message));
    }
  };

  // Construir y abrir una ventana para reimprimir un ticket no-fiscal
  const printNonFiscalTicket = (sale) => {
    try {
      if (!sale) return;

      const leftName = typeof window !== 'undefined' ? localStorage.getItem('ticketLeftName') : null;
      const logoData = typeof window !== 'undefined' ? localStorage.getItem('ticketLogoData') : null;

      const fallbackName = tenant?.businessName || user?.tenantId?.brandName || user?.tenantId?.name || 'Comprobante';

      // Always generate fresh HTML to avoid escaped HTML issues
      const html = buildNonFiscalTicketHtml(sale, {
        settingsLogoData: logoData,
        settingsLeftName: leftName || fallbackName,
        ticketHeader: process.env.REACT_APP_TICKET_HEADER || '',
        emitterIsMonotributo
      });


      // Create hidden iframe for printing
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      document.body.appendChild(iframe);

      const iframeDoc = iframe.contentWindow.document;
      iframeDoc.open();
      iframeDoc.write(html);
      iframeDoc.close();

      // Wait for images to load, then print
      const waitForImages = (win, timeout = 3000) => new Promise((resolve) => {
        try {
          const imgs = Array.from(win.document.images || []);
          if (imgs.length === 0) return resolve(true);
          let remaining = imgs.length;
          const onDone = () => {
            remaining -= 1;
            if (remaining === 0) resolve(true);
          };
          imgs.forEach((img) => {
            if (img.complete && img.naturalWidth !== 0) {
              remaining -= 1;
              if (remaining === 0) resolve(true);
              return;
            }
            img.addEventListener('load', onDone);
            img.addEventListener('error', onDone);
          });
          setTimeout(() => resolve(true), timeout);
        } catch (e) { resolve(true); }
      });

      waitForImages(iframe.contentWindow, 2500).then(() => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
          // Remove iframe after printing
          setTimeout(() => {
            try {
              document.body.removeChild(iframe);
            } catch (e) {
              console.error('Error removing iframe:', e);
            }
          }, 1000);
        } catch (e) {
          console.error('print error', e);
          document.body.removeChild(iframe);
        }
      });
    } catch (e) {
      console.error('Error reimprimiendo ticket:', e);
      toast.error('Error al intentar reimprimir ticket');
    }
  };

  // No local escape helper needed; template builder performs escaping

  return (
    <div className="container-safe" style={{ padding: '30px', fontFamily: 'Arial, sans-serif' }}>
      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>📊 Historial de Ventas</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button style={btnNav} onClick={() => navigate('/')}>🏠 Dashboard</button>
          {!isEcommerceLite && (
            <button style={{ ...btnNav, background: '#28a745' }} onClick={() => navigate('/sales')}>💵 Nueva Venta</button>
          )}
          <button style={{ ...btnNav, background: '#007bff' }} onClick={() => navigate('/products')}>📦 Productos</button>
          <button style={{ ...btnNav, background: '#dc3545' }} onClick={() => navigate('/login')}>🚪 Salir</button>
        </div>
      </div>

      {/* Filtro rápido por fuente */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <button
          onClick={() => {
            setFilterSource('all');
            setCurrentPage(1);
          }}
          style={{ padding: '8px 18px', borderRadius: '20px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '14px', background: filterSource === 'all' ? '#333' : '#e9ecef', color: filterSource === 'all' ? '#fff' : '#555', transition: 'all 0.2s' }}
        >
          Todas
        </button>
        <button
          onClick={() => {
            setFilterSource('online');
            setCurrentPage(1);
          }}
          style={{ padding: '8px 18px', borderRadius: '20px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '14px', background: filterSource === 'online' ? '#1976d2' : '#e3f2fd', color: filterSource === 'online' ? '#fff' : '#1976d2', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          💻 Pedidos Online
        </button>
        <button
          onClick={() => {
            setFilterSource('local');
            setCurrentPage(1);
          }}
          style={{ padding: '8px 18px', borderRadius: '20px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '14px', background: filterSource === 'local' ? '#28a745' : '#e9ecef', color: filterSource === 'local' ? '#fff' : '#555', transition: 'all 0.2s' }}
        >
          🏧 Ventas Locales
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Cargando ventas...</p>
        </div>
      ) : (sales.length === 0 && currentPage === 1) ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>No hay ventas registradas aún.</p>
          {!isEcommerceLite && (
            <button style={{ ...btnNav, background: '#28a745', marginTop: '20px' }} onClick={() => navigate('/sales')}>
              ➕ Realizar primera venta
            </button>
          )}
        </div>
      ) : (
        <div>
          {/* Filtros */}
          <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: '15px', fontSize: '16px' }}>🔍 Filtros</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
              <div>
                <label style={{ fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Fecha desde:</label>
                <input
                  type="date"
                  value={filterDateFrom}
                  onChange={(e) => {
                    setFilterDateFrom(e.target.value);
                    setCurrentPage(1);
                  }}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={{ fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Fecha hasta:</label>
                <input
                  type="date"
                  value={filterDateTo}
                  onChange={(e) => {
                    setFilterDateTo(e.target.value);
                    setCurrentPage(1);
                  }}
                  style={inputStyle}
                />
              </div>
            </div>
            <button
              style={{ ...btnSmall, marginTop: '10px', background: '#6c757d' }}
              onClick={clearFilters}
            >
              🗑️ Limpiar filtros
            </button>
          </div>

          <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <p style={{ fontSize: '16px', color: '#666' }}>Total de ventas: {totalItems}</p>
            <p style={{ fontSize: '16px', color: '#666' }}>
              Total acumulado: ${totalAmount.toFixed(2)}
            </p>
          </div>

          <div className="responsive-table">
            <table style={tableStyle}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={thStyle}>Fecha</th>
                  <th style={thStyle}>Origen</th>
                  <th style={thStyle}>Código</th>
                  <th style={thStyle}>Items</th>
                  <th style={thStyle}>Total</th>
                  <th style={thStyle}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {sales.map((sale) => (
                  <React.Fragment key={sale._id}>
                    <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                      <td style={tdStyle}>{formatDate(sale.createdAt || sale.date)}</td>
                      <td style={tdStyle}>
                        {sale.source === 'ecommerce' ? (
                          <span style={{
                            background: '#e3f2fd',
                            color: '#1976d2',
                            padding: '4px 8px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            border: '1px solid #bbdefb'
                          }}> ONLINE 💻</span>
                        ) : sale.source === 'tiendanube' ? (
                          <span style={{
                            background: '#e0f7fa',
                            color: '#006064',
                            padding: '4px 8px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            border: '1px solid #b2ebf2'
                          }}> TIENDANUBE ☁️</span>
                        ) : sale.source === 'mercadolibre' ? (
                          <span style={{
                            background: '#fff8e1',
                            color: '#ff8f00',
                            padding: '4px 8px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            border: '1px solid #ffe082'
                          }}> M. LIBRE 🤝</span>
                        ) : (
                          <span style={{ color: '#666', fontSize: '12px' }}>Local 🏪</span>
                        )}
                      </td>
                      <td style={tdStyle}>
                        {sale.verificationCode ? (
                          <span style={{
                            fontFamily: 'monospace',
                            fontWeight: '900',
                            color: '#b45309',
                            background: '#fffbeb',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            border: '1px solid #fcd34d',
                            fontSize: '14px'
                          }}>
                            {sale.verificationCode}
                          </span>
                        ) : (
                          <span style={{ color: '#ccc' }}>-</span>
                        )}
                      </td>
                      <td style={tdStyle}>{sale.items.length} producto{sale.items.length > 1 ? 's' : ''}</td>
                      <td style={tdStyle}>${sale.total.toFixed(2)}</td>
                      <td style={tdStyle}>
                        <button
                          style={btnSmall}
                          onClick={() => toggleExpand(sale._id)}
                        >
                          {expandedSale === sale._id ? '▲ Ocultar' : '▼ Ver detalles'}
                        </button>
                        {/* Ocultar botón Facturar si ya existe una factura asociada */}
                        {!sale.invoiceExists && (
                          <button
                            style={{ ...btnSmall, marginLeft: '5px', background: '#667eea' }}
                            onClick={() => {
                              setSelectedSale(sale);
                              setShowInvoiceModal(true);
                            }}
                          >
                            🧾 Facturar
                          </button>
                        )}
                        {/* Reimprimir ticket no-fiscal: sólo mostrar si NO existe factura */}
                        {!sale.invoiceExists && (
                          <>
                            <button
                              style={{ ...btnSmall, marginLeft: '8px', background: '#6c757d' }}
                              onClick={() => printNonFiscalTicket(sale)}
                              title="Reimprimir ticket no fiscal"
                            >
                              🖨️ Reimprimir ticket
                            </button>
                            <button
                              style={{ ...btnSmall, marginLeft: '8px', background: '#dc3545' }}
                              onClick={() => cancelSale(sale._id)}
                              title="Cancelar venta (solo para ventas no fiscales)"
                            >
                              ❌ Cancelar
                            </button>
                          </>
                        )}
                        {sale.invoiceExists && (
                          <span style={{ marginLeft: '8px', color: '#28a745', fontWeight: 'bold' }}>Facturada</span>
                        )}
                      </td>
                    </tr>
                    {expandedSale === sale._id && (
                      <tr style={{ backgroundColor: '#f8f9fa' }}>
                        <td colSpan="6" style={{ padding: '15px' }}>
                          <div style={{ marginLeft: '20px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                              <h4 style={{ margin: 0 }}>Detalle de productos:</h4>
                              {sale.verificationCode && (
                                <div style={{
                                  background: '#fffbeb',
                                  border: '1px solid #fcd34d',
                                  padding: '8px 15px',
                                  borderRadius: '8px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '10px'
                                }}>
                                  <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#92400e' }}>🔑 Código Verificación:</span>
                                  <span style={{ fontSize: '18px', fontWeight: '900', color: '#b45309', letterSpacing: '1px' }}>{sale.verificationCode}</span>
                                </div>
                              )}
                            </div>

                            {/* Datos del cliente si es pedido online */}
                            {sale.source !== 'local' && sale.source && sale.shippingDetails && (
                              <div style={{ background: '#e3f2fd', border: '1px solid #bbdefb', borderRadius: '8px', padding: '12px 16px', marginBottom: '12px' }}>
                                <div style={{ fontWeight: '700', color: '#1565c0', marginBottom: '8px', fontSize: '13px' }}>💻 Datos del pedido online</div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '6px', fontSize: '13px', color: '#333' }}>
                                  {sale.shippingDetails.customerName && <div><b>Cliente:</b> {sale.shippingDetails.customerName}</div>}
                                  {sale.shippingDetails.contactPhone && <div><b>Teléfono:</b> {sale.shippingDetails.contactPhone}</div>}
                                  {sale.shippingDetails.contactEmail && <div><b>Email:</b> {sale.shippingDetails.contactEmail}</div>}
                                  <div><b>Envío:</b> {sale.shippingDetails.type === 'delivery' ? '🚚 Domicilio' : '🏧 Retiro en local'}</div>
                                  {sale.shippingDetails.type === 'delivery' && sale.shippingDetails.address && (
                                    <div style={{ gridColumn: '1 / -1' }}>
                                      <b>Dirección:</b> {[
                                        sale.shippingDetails.address.street,
                                        sale.shippingDetails.address.number,
                                        sale.shippingDetails.address.city,
                                        sale.shippingDetails.address.province
                                      ].filter(Boolean).join(', ')}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            <table style={{ width: '100%', marginTop: '10px' }}>
                              <thead>
                                <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                                  <th style={{ ...thStyle, backgroundColor: 'transparent' }}>Producto</th>
                                  <th style={{ ...thStyle, backgroundColor: 'transparent' }}>Cantidad</th>
                                  <th style={{ ...thStyle, backgroundColor: 'transparent' }}>Precio Unit.</th>
                                  <th style={{ ...thStyle, backgroundColor: 'transparent' }}>Subtotal</th>
                                </tr>
                              </thead>
                              <tbody>
                                {sale.items.map((item, idx) => (
                                  <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                    <td style={tdStyle}>{getProductName(item)}</td>
                                    <td style={tdStyle}>{item.quantity}</td>
                                    <td style={tdStyle}>${item.price.toFixed(2)}</td>
                                    <td style={tdStyle}>${(item.quantity * item.price).toFixed(2)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          {totalPages > 1 && (
            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '15px' }}>
              <button
                style={{ ...btnSmall, background: currentPage === 1 ? '#ccc' : '#007bff' }}
                disabled={currentPage === 1}
                onClick={() => fetchSales(currentPage - 1)}
              >
                Anterior
              </button>
              <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
                Página {currentPage} de {totalPages}
              </span>
              <button
                style={{ ...btnSmall, background: currentPage === totalPages ? '#ccc' : '#007bff' }}
                disabled={currentPage === totalPages}
                onClick={() => fetchSales(currentPage + 1)}
              >
                Siguiente
              </button>
            </div>
          )}
        </div>
      )}

      {/* Modal de facturación */}
      {showInvoiceModal && selectedSale && (
        <InvoiceFromSaleModal
          sale={selectedSale}
          onClose={() => {
            setShowInvoiceModal(false);
            setSelectedSale(null);
          }}
          onSuccess={() => {
            toast.success('✅ Factura generada exitosamente');
            // Marcar la venta como facturada en la lista sin requerir refresco manual
            const id = selectedSale && selectedSale._id;
            if (id) {
              setSales((prev) => prev.map(s => s._id === id ? { ...s, invoiceExists: true } : s));
            }
            // Opcionalmente, refrescar en background para sincronizar con el servidor
            try { fetchSales(); } catch (_) { /* no bloquear UI */ }
          }}
        />
      )}
    </div>
  );
};

// 🔹 Estilos
const btnNav = {
  background: '#6c757d',
  color: 'white',
  border: 'none',
  padding: '8px 12px',
  borderRadius: '6px',
  cursor: 'pointer',
  fontWeight: 'bold',
  fontSize: '14px',
};

const btnSmall = {
  background: '#007bff',
  color: 'white',
  border: 'none',
  padding: '6px 10px',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '13px',
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  marginTop: '10px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  backgroundColor: 'white',
};

const thStyle = {
  padding: '12px',
  textAlign: 'left',
  fontWeight: 'bold',
  borderBottom: '2px solid #dee2e6',
};

const tdStyle = {
  padding: '12px',
  textAlign: 'left',
};

const inputStyle = {
  padding: '8px',
  borderRadius: '4px',
  border: '1px solid #ccc',
  width: '100%',
  fontSize: '14px',
};

export default SalesHistory;
